import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SectionHeading } from "@/components/site/SectionHeading";
import { DiscordMessageCard } from "@/components/site/DiscordMessageCard";
import { useContent } from "@/context/ContentContext";
import { useText } from "@/lib/useText";
import { VideoBackdrop } from "@/components/site/VideoBackdrop";

const TAB_IDS = ["updates", "giveaways", "events"];

export default function Novidades() {
  const [params, setParams] = useSearchParams();
  const { content } = useContent();
  const t = useText();
  const TABS = TAB_IDS.map((id) => ({ id, label: t(`news_tab_${id}`) }));
  const tab = TABS.some((t) => t.id === params.get("tab")) ? params.get("tab") : "updates";
  const [feed, setFeed] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/discord/feed?type=${tab}`);
        if (alive) { setFeed((f) => ({ ...f, [tab]: data })); setUpdatedAt(new Date()); }
      } catch {
        if (alive) setFeed((f) => ({ ...f, [tab]: { messages: [], error: "unavailable" } }));
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [tab]);

  const current = feed[tab];

  return (
    <section className="relative py-16 sm:py-24" aria-labelledby="novidades-title" data-testid="news-page">
      <VideoBackdrop opacity={0.22} className="h-[420px]" />
      <div className="container-eden relative">
        <SectionHeading id="novidades-title" eyebrow={t("news_eyebrow")} title={t("news_title")} text={t("news_text")} />
        <div className="mt-10 flex items-center justify-between gap-4 border-b border-eden-border">
          <div role="tablist" aria-label="Tipo de publicações" className="-mb-px flex gap-6">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                type="button"
                aria-selected={tab === t.id}
                aria-controls={`panel-${t.id}`}
                id={`tab-${t.id}`}
                onClick={() => setParams({ tab: t.id }, { replace: true })}
                className={`border-b-2 pb-3 font-display text-sm font-semibold uppercase tracking-wider transition-colors ${tab === t.id ? "border-eden-orange text-eden-text" : "border-transparent text-eden-muted hover:text-eden-secondary"}`}
                data-testid={`news-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {updatedAt && <p className="hidden pb-3 font-mono text-[11px] uppercase tracking-widest text-eden-muted sm:block" data-testid="news-updated-at">Atualizado {updatedAt.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}</p>}
        </div>

        <div id={`panel-${tab}`} role="tabpanel" aria-labelledby={`tab-${tab}`} className="mt-10">
          {!current && <p className="text-sm text-eden-muted" role="status">A carregar publicações…</p>}
          {current?.error && (
            <div className="card-eden p-8 text-center" role="alert" data-testid="news-error">
              <p className="text-eden-secondary">{t("news_error")}</p>
              <a href={content.links.discord_invite} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block text-sm text-eden-orange underline underline-offset-4">{t("news_open_discord")}</a>
            </div>
          )}
          {current && !current.error && current.messages.length === 0 && (
            <div className="card-eden p-10 text-center" data-testid="news-empty">
              <p className="font-display text-lg font-semibold text-eden-text">{t("news_empty_title")}</p>
              <p className="mt-2 text-sm text-eden-muted">{t("news_empty_text")}</p>
            </div>
          )}
          {current && !current.error && current.messages.length > 0 && (
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="news-grid">
              {current.messages.map((m) => <DiscordMessageCard key={m.id} msg={m} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
